import type { CamFields, Fitting } from './assemblyStore';
import { qRotate, quatFromEulerDeg, type Vec3 } from './assemblyPose';
import { loadFit } from './holeFit';
import { MM_PER_TRUE_UNIT } from './meshAnnotations';
import { loadDeps } from './meshScene';
import { builtBoxOf } from './openaiBlenderApi';
import { isTrueScale, meshSrc, meshStamp, type Job3D } from './threeDApi';
import { SHOT_RATIO } from './shotShape';

// THE SHOT OF A MOVEMENT WORKED OUT BY RULE — a part placed as its joint has it, a pair as its set
// built it, a piece of hardware into its hole (see fromJoint, fromSet, fromHole).
//
// ACROSS THE MOVEMENT, NEVER ALONG IT. Square-on stands in front of the product looking back, and
// a rail brought onto the rear frame travels straight away from that camera: four seconds of a part
// getting slightly smaller. So the camera stands square to the way the part comes in — a level
// movement runs across the picture, an upright one up it — and then BIAS_DEG round, toward where the
// part starts from, so the face it lands on is seen and the parts read as solids.
//
// LEVEL, ALWAYS: the camera is turned about the product's upright and never tipped, so what stands
// upright is drawn upright.
//
// UNLESS THE PART CANNOT BE SEEN FROM THERE. A screw driven up into a seat rail a hand's width
// behind a front leg, watched from the front like every upright movement, was four seconds of the
// leg. So where the caller can say how much of the moving part each side hides (see hiddenFrom), every
// side is measured — that one, the same leaning the other way, the two from the far side, each from
// above and from below — and the one that hides the least of it is the shot, whatever its place in
// the list (Louis, 2026-10-22: the order is no reason). A tie goes to the earlier.
// Where nothing is in the way, that is the first, and nothing has changed.
//
// ON THE SIDE THE MOVING PART IS ON, so the part it goes onto is behind it and not in front of it.
// Aimed at the middle of its way in, and held there: nothing moves but the part.
//
// AS CLOSE AS THE ACTION ASKS. Zoom 1 is the scene's own fit of both parts over the whole movement
// from that angle (see `look`), and a barrel nut going into a frame is then two hundredths of the
// picture. So the picture is of the MOVING part (see zoomOf) — never tighter than FRAME_ACTIONS
// times the way in and the part's thickness, which is what leaves a nut its surroundings, never
// wider than that fit, and never closer than MAX_ZOOM, where a washer would be filmed with nothing
// around it to say where it is.
const BIAS_DEG = 25;
const FRAME_ACTIONS = 4;
// What counts as small: its longest side under this share of the longest side of what it goes onto.
const SMALL_SHARE = 0.25;
const MAX_ZOOM = 8;
// What the scene's fit leaves around the parts, and the picture's shape it is fitted into — near
// enough: the zoom is a framing, not a measurement.
const FIT_MARGIN = 1.2;
// And what is left around the moving part's own way in.
const HELD_MARGIN = 1.1;
// AND ROOM FOR THE HAND, where a tool rides on it: the screwdriver turning a barrel nut was framed
// to its own length, the nut filling the picture and the rail it goes into a strip of wood.
const TOOL_ROOM = 2;
// HOW WIDE THE PICTURE IS FOR ITS HEIGHT: the box every shot is drawn in (see shotShape).
const ASPECT = SHOT_RATIO;

// EACH PART'S BOX, in millimetres — what the zoom is worked out from. Either unknown, and the shot
// stays at the scene's fit. AND THE TOOL'S, where it rides on the moving part (see Fitting.toolRide):
// its box and where its middle stands from the moving part's, in the moving part's frame. A barrel
// nut framed alone was a nut filling the picture, the screwdriver turning it cut off at the edge.
// AND THE WIDTH ZOOM 1 SHOWS, measured on the scene that will draw it (see AssemblyView.widthAt):
// the viewer fits zoom 1 to the parts' own outline, and an estimate from their boxes stood further
// back than it, so every zoom came out tighter than the rule meant (Louis 2026-10-28). Absent, or
// null, and the boxes' estimate it was.
export type ShotSizes = {
  target: Vec3 | null; moving: Vec3 | null; tool?: { size: Vec3; at: Vec3 } | null;
  fit?: ((eye: Vec3, aim: Vec3) => number | null) | null;
};

// A MODEL'S OWN BOX: what its build measured, or the file itself where it is at true scale (a baked
// piece). Null for a model put into millimetres by a listed size.
export async function jobBoxMm(job: Job3D): Promise<Vec3 | null> {
  const built = builtBoxOf(job);
  if (built) return [built.x, built.y, built.z];
  if (!isTrueScale(job)) return null;
  const { THREE, GLTFLoader } = await loadDeps();
  const mesh = await loadFit(new GLTFLoader(), meshSrc(job.id, meshStamp(job)));
  if (!mesh) return null;
  const size = new THREE.Box3().setFromObject(mesh).getSize(new THREE.Vector3()).multiplyScalar(MM_PER_TRUE_UNIT);
  return [size.x, size.y, size.z];
}

const norm = (v: Vec3): Vec3 => {
  const l = Math.hypot(...v) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};

// AND EVERY COPY, where the gesture puts several on (see Fitting.copies): four felt pads under
// four feet were framed on the first, and the other three were off the picture.
export type Shot = Pick<Fitting, 'seat' | 'axis' | 'travel' | 'targetSeat'> & Partial<Pick<Fitting, 'spin' | 'copies'>>;

// EACH UNIT'S WAY IN, the first and its copies: where it rests and where it starts, from the fixed
// part's middle.
const waysOf = (m: Shot): { home: Vec3; from: Vec3 }[] => [m, ...(m.copies ?? [])].map((u) => {
  const a = norm(u.axis);
  const home: Vec3 = [m.targetSeat[0] + u.seat[0], m.targetSeat[1] + u.seat[1], m.targetSeat[2] + u.seat[2]];
  return { home, from: [home[0] + a[0] * u.travel, home[1] + a[1] * u.travel, home[2] + a[2] * u.travel] };
});

// THE MOVING PART AS IT STANDS, turned, and the tool riding on it: one box round both, square to the
// assembly (`stood`), and where its middle stands from the moving part's (`shift`).
function ridingBox(m: Shot, moving: Vec3, tool?: ShotSizes['tool']) {
  const turn = quatFromEulerDeg(m.spin ?? [0, 0, 0]);
  const sides = ([[1, 0, 0], [0, 1, 0], [0, 0, 1]] as Vec3[]).map((e) => qRotate(turn, e));
  const dot = (u: Vec3, v: Vec3) => u[0] * v[0] + u[1] * v[1] + u[2] * v[2];
  const own = (dir: Vec3, size: Vec3) => sides.reduce((sum, side, i) => sum + Math.abs(dot(dir, side)) * size[i], 0);
  const ownStood: Vec3 = [own([1, 0, 0], moving), own([0, 1, 0], moving), own([0, 0, 1], moving)];
  const at = tool ? qRotate(turn, tool.at) : null;
  const toolStood: Vec3 | null = tool ? [own([1, 0, 0], tool.size), own([0, 1, 0], tool.size), own([0, 0, 1], tool.size)] : null;
  const lo = [0, 1, 2].map((i) => Math.min(-ownStood[i] / 2, at && toolStood ? at[i] - toolStood[i] / 2 : Infinity));
  const hi = [0, 1, 2].map((i) => Math.max(ownStood[i] / 2, at && toolStood ? at[i] + toolStood[i] / 2 : -Infinity));
  const stood: Vec3 = [hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]];
  const shift: Vec3 = [(hi[0] + lo[0]) / 2, (hi[1] + lo[1]) / 2, (hi[2] + lo[2]) / 2];
  const span = (dir: Vec3) => (tool ? Math.abs(dir[0]) * stood[0] + Math.abs(dir[1]) * stood[1] + Math.abs(dir[2]) * stood[2]
    : own(dir, moving));
  return { stood, shift, span };
}

// WHAT THE SCENE'S FIT HOLDS, where it is more than this movement's two parts: each box's middle
// and size, in millimetres in the assembly's frame. The barrel nuts going into the rail are a
// movement of their own inside the rail's onto the frame, and zoom 1 is that whole scene's fit.
export type Room = { at: Vec3; size: Vec3 }[];

// AND HOW WIDE THE PICTURE IS THEN, in millimetres across: how far off the camera stands (see
// reachOf). Unknown sizes, the scene's own fit and no width to say.
function zoomOf(m: Shot, eye: Vec3, aim: Vec3, sizes?: ShotSizes, room?: Room, from: Vec3 = eye): { zoom: number; width: number } {
  if (!sizes?.target || !sizes.moving) return { zoom: 1, width: Infinity };
  const a = norm(m.axis);
  const tool = sizes.tool;
  const { stood, shift, span } = ridingBox(m, sizes.moving, tool);
  // WHAT THE SCENE'S FIT HOLDS, as a width: across the picture and up it, from the aim, how far the
  // furthest corner of anything stands.
  let half = 0;
  const reach = (c: Vec3, size: Vec3) => {
    for (const sx of [-1, 1]) for (const sy of [-1, 1]) for (const sz of [-1, 1]) {
      const d: Vec3 = [c[0] + (sx * size[0]) / 2 - aim[0], c[1] + (sy * size[1]) / 2 - aim[1], c[2] + (sz * size[2]) / 2 - aim[2]];
      half = Math.max(half, Math.abs(d[1] * eye[0] - d[0] * eye[1]), Math.abs(d[2]) * ASPECT);
    }
  };
  const home: Vec3 = [
    m.targetSeat[0] + m.seat[0] + shift[0], m.targetSeat[1] + m.seat[1] + shift[1], m.targetSeat[2] + m.seat[2] + shift[2],
  ];
  if (room) {
    for (const b of room) reach(b.at, b.size);
  } else {
    reach(m.targetSeat, sizes.target);
    reach(home, stood);
    reach([home[0] + a[0] * m.travel, home[1] + a[1] * m.travel, home[2] + a[2] * m.travel], stood);
    for (const w of waysOf(m).slice(1)) { reach(w.home, stood); reach(w.from, stood); }
  }
  // From where the camera will actually stand (`from`, tipped where the side is), on the scene.
  const fit = sizes.fit?.(from, aim) ?? 2 * half * FIT_MARGIN;

  // WHAT THE PICTURE HAS TO HOLD: the moving part over its whole way in, and a strip of the other —
  // as wide as the moving part is thick — where the two meet. The rest of the part it goes onto may
  // run off the picture: a rail brought onto a frame 73 cm tall was framed by the frame.
  const upright = Math.abs(a[2]) > Math.hypot(a[0], a[1]);
  const level: Vec3 = upright ? [1, 0, 0] : norm([-a[1], a[0], 0]);
  const rise: Vec3 = [a[1] * level[2] - a[2] * level[1], a[2] * level[0] - a[0] * level[2], a[0] * level[1] - a[1] * level[0]];
  const thick = Math.max(span(level), span(rise));
  const swept = m.travel + span(a);
  // Along the picture's width for a level movement (seen BIAS_DEG off square), up it for an upright one.
  const k = Math.cos((BIAS_DEG * Math.PI) / 180);
  const held = (upright ? ASPECT : k) * swept + 2 * thick * (upright ? ASPECT : 1);
  const across = (upright ? Math.max(stood[0], stood[1]) : stood[2] * ASPECT) * 1.5;
  // AND ALL THE COPIES AT ONCE: how far apart their ways in stand, across the picture, up it, AND
  // ALONG IT. A view looking down the line they stand along (the one left where every view square to
  // it was hidden) sees that spread as depth, tipped up the picture or off its side, and measured
  // across alone it read as none: the four dowels of a 45 cm frame were framed ×8 on the near pair.
  const side: Vec3 = norm([-eye[1], eye[0], 0]);
  const ends = waysOf(m).flatMap((w) => [w.home, w.from]);
  const spanOf = (f: (c: Vec3) => number) => Math.max(...ends.map(f)) - Math.min(...ends.map(f));
  const apart = (Math.max(
    spanOf((c) => c[0] * side[0] + c[1] * side[1]),
    spanOf((c) => c[2]) * ASPECT,
    spanOf((c) => c[0] * eye[0] + c[1] * eye[1]),
  ) + 2 * thick) * HELD_MARGIN;
  // FRAME_ACTIONS is for a SMALL part: four times a barrel nut is a few centimetres of rail around
  // it, four times the front legs frame was a picture two metres wide around a 45 cm frame (×1.06).
  const small = Math.max(...sizes.moving) < SMALL_SHARE * Math.max(...sizes.target);
  const wide = Math.max(small ? FRAME_ACTIONS * (m.travel + thick) : 0, held * HELD_MARGIN * (tool ? TOOL_ROOM : 1), across, apart);
  if (!(wide > 0)) return { zoom: 1, width: fit };
  const zoom = Math.round(Math.min(MAX_ZOOM, Math.max(1, fit / wide)) * 100) / 100;
  return { zoom, width: fit / zoom };
}

// HOW FAR OFF THE CAMERA STANDS from what it looks at, for a picture this wide: the viewer's
// lens (40° up, the picture ASPECT times as wide as it is tall; see assemblyScene's camera). An
// estimate of where `look` will put it, near enough for what stands between.
const HALF_WIDE = Math.tan((20 * Math.PI) / 180) * ASPECT;
const reachOf = (width: number) => width / (2 * HALF_WIDE);

// How far above or below level a side is tried from, where every level one is hidden.
const TIP_DEG = 35;

// HOW MUCH OF THE MOVING PART EACH OF THESE SIDES HIDES — 0 none of it, 1 all (see hiddenFrom).
// `cams`, per side: WHERE THE CAMERA STANDS, in the assembly's millimetres. Each point is looked
// at from there, and nothing beyond it hides anything: the backrest standing behind a camera close
// in on the dowels counted as hiding them all, and one distance for every point, measured from the
// middle of the shot, stopped the far pair's lines short of the leg in front of them.
export type HiddenFrom = (eyes: Vec3[], cams?: Vec3[]) => Promise<number[]>;

// WHERE THE CAMERA STANDS FROM WHAT IT LOOKS AT, in the assembly's frame (Z up, −Y the front): the
// sides it may be watched from, the rule's own first.
// AND WHAT EACH IS, said as it is made: what the candidate views shown under the viewer are
// labelled with (see ShotCandidates).
// One line per thing it is set by: the rule, the side, the turn, the height.
type Eye = { eye: Vec3; label: string[] };
function eyesOf(m: Pick<Fitting, 'seat' | 'axis'>): Eye[] {
  const a = norm(m.axis);
  const upright = Math.abs(a[2]) > Math.hypot(a[0], a[1]);
  // The way it comes in, laid level — and for an upright movement the product's width, on the
  // moving part's side.
  const along: Vec3 = upright ? [m.seat[0] < -1 ? -1 : 1, 0, 0] : norm([a[0], a[1], 0]);
  // Square to it, level: from the front for an upright movement, unless the moving part is behind.
  let across: Vec3 = upright ? [0, -1, 0] : [-along[1], along[0], 0];
  const side = across[0] * m.seat[0] + across[1] * m.seat[1];
  // Where the two are in line, the side nearer the front, then the product's left.
  const facing = Math.abs(side) > 1 ? side : -across[1] || -across[0];
  if (facing < 0) across = [-across[0], -across[1], 0];
  const k = (BIAS_DEG * Math.PI) / 180;
  const from = (side: number, lean: number): Vec3 => norm([
    side * across[0] * Math.cos(k) + lean * along[0] * Math.sin(k),
    side * across[1] * Math.cos(k) + lean * along[1] * Math.sin(k),
    0,
  ]);
  const level = [
    { eye: from(1, 1), label: ['Across the way in', 'Moving part\'s side', 'Turned 25° toward its start'] },
    { eye: from(1, -1), label: ['Across the way in', 'Moving part\'s side', 'Turned 25° away from its start'] },
    { eye: from(-1, 1), label: ['Across the way in', 'Far side', 'Turned 25° toward its start'] },
    { eye: from(-1, -1), label: ['Across the way in', 'Far side', 'Turned 25° away from its start'] },
  ];
  const tip = Math.tan((TIP_DEG * Math.PI) / 180);
  const tipped = (up: number) => level.map((e): Eye => ({
    eye: norm([e.eye[0], e.eye[1], up * tip]),
    label: [...e.label, `${TIP_DEG}° ${up > 0 ? 'above' : 'below'}`],
  }));
  return [...level.map((e) => ({ ...e, label: [...e.label, 'Level'] })), ...tipped(1), ...tipped(-1)];
}

// UNITS SPREAD WIDER THAN THEIR WAY IN — a wood button on each rear leg, a chair's width apart —
// are watched from where they stand side by side, not from the side of the way in: from there the
// two pairs stood one behind the other, the far one hidden by the frame, and the zoom framed the
// near one. So: square to the line they stand along, on the side they come from, a little above
// first. Nothing for a single unit or units close together (the barrel nuts of one rail).
const SPREAD_OVER_WAY = 2;
function spreadEyes(m: Shot): Eye[] {
  const homes = waysOf(m).map((w) => w.home);
  if (homes.length < 2) return [];
  let far: [Vec3, Vec3] = [homes[0], homes[0]];
  let most = 0;
  for (const a of homes) for (const b of homes) {
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (d > most) { most = d; far = [a, b]; }
  }
  if (most <= SPREAD_OVER_WAY * Math.max(1, m.travel)) return [];
  const along = norm([far[1][0] - far[0][0], far[1][1] - far[0][1], 0]);
  let square: Vec3 = [-along[1], along[0], 0];
  // On the side they come from; for an upright way in, the front.
  const a = norm(m.axis);
  const from: Vec3 = Math.abs(a[2]) > Math.hypot(a[0], a[1]) ? [0, -1, 0] : a;
  if (square[0] * from[0] + square[1] * from[1] < 0) square = [-square[0], -square[1], 0];
  const tip = Math.tan((TIP_DEG * Math.PI) / 180);
  const said = ['Units spread wide', 'Square to the line they stand along', 'From the side they come from'];
  return [
    { eye: norm([square[0], square[1], tip]), label: [...said, `${TIP_DEG}° above`] },
    { eye: square, label: [...said, 'Level'] },
    { eye: norm([square[0], square[1], -tip]), label: [...said, `${TIP_DEG}° below`] },
  ];
}

// EVERY VIEW THE RULE WEIGHS, each as a shot, with how much of the moving part it hides (null where
// nobody could say) and the one it takes: the one hiding the least, or the first where nothing is
// known. There was a slack of 15 points in favour of the earlier views, and it made the order of the
// list decide: on 1-1 to 2-1, 11 shots of 12 were the first or second view tried. What movementShot returns, and what the
// candidate views under the viewer show (see ShotCandidates).
export type ShotCandidate = { label: string[]; eye: Vec3; hidden: number | null; cam: CamFields };

// SAID AS A FITTING STORES A SHOT: an aim in the assembly's frame, degrees off the square-on view,
// a multiple of the fitted distance. The square-on view stands on the assembly's −Y looking down +Y
// with Z up, in a world that is the assembly turned Z-up to Y-up (see assemblyScene).
export async function shotCandidates(
  m: Shot,
  sizes?: ShotSizes,
  // Absent, the rule's own side: nothing is known about what is in the way.
  hidden?: HiddenFrom,
  // Absent, the scene is this movement's two parts (see Room).
  room?: Room,
): Promise<{ list: ShotCandidate[]; picked: number }> {
  const { THREE } = await loadDeps();
  const root = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
  const world = (v: Vec3) => new THREE.Vector3(...v).applyQuaternion(root);
  const look = (from: import('three').Vector3) => {
    const probe = new THREE.PerspectiveCamera();
    probe.up.copy(world([0, 0, 1]));
    probe.position.copy(from);
    probe.lookAt(0, 0, 0);
    return probe.quaternion.clone();
  };
  const eyes = [...spreadEyes(m), ...eyesOf(m)];
  const a = norm(m.axis);
  // ON THE MIDDLE OF THE WAY IN — of the moving part with its tool, where one rides on it: aimed at
  // the nut, the screwdriver turning it reached off the side of the picture on the first frame.
  const shift = sizes?.moving ? ridingBox(m, sizes.moving, sizes.tool).shift : [0, 0, 0];
  // Where there are copies, on the middle of all their ways in.
  const ways = waysOf(m);
  const mid = (i: number) => Math.round((ways.length > 1
    ? (Math.min(...ways.flatMap((w) => [w.home[i], w.from[i]])) + Math.max(...ways.flatMap((w) => [w.home[i], w.from[i]]))) / 2
    : m.targetSeat[i] + m.seat[i] + shift[i] + (a[i] * m.travel) / 2) * 10) / 10;
  const aim = (): Vec3 => [mid(0), mid(1), mid(2)];
  // The zoom is worked out across the picture, which a tipped side sees as its level one does.
  const framing = eyes.map(({ eye }) => zoomOf(m, norm([eye[0], eye[1], 0]), aim(), sizes, room, norm(eye)));
  const camAt = (eye: Vec3, width: number): Vec3 => {
    const d = reachOf(width);
    const at = aim();
    return [at[0] + eye[0] * d, at[1] + eye[1] * d, at[2] + eye[2] * d];
  };
  const by = hidden
    ? await hidden(eyes.map((e) => e.eye), eyes.map((e, i) => camAt(norm(e.eye), framing[i].width)))
    : null;
  const picked = by ? by.indexOf(Math.min(...by)) : 0;
  const camOf = (eye: Vec3, zoom: number): CamFields => {
    const rel = look(world([0, -1, 0])).invert().multiply(look(world(eye)));
    const e = new THREE.Euler().setFromQuaternion(rel, 'XYZ');
    const deg = (r: number) => Math.round((r * 180) / Math.PI);
    const rot: Vec3 = [deg(e.x), deg(e.y), deg(e.z)];
    return {
      camStartPos: aim(), camEndPos: aim(), camStartRot: rot, camEndRot: [...rot] as Vec3,
      camStartZoom: zoom, camEndZoom: zoom,
    };
  };
  return {
    list: eyes.map((e, i) => ({ label: e.label, eye: e.eye, hidden: by?.[i] ?? null, cam: camOf(e.eye, framing[i].zoom) })),
    picked,
  };
}

// THE ONE IT TAKES.
export async function movementShot(m: Shot, sizes?: ShotSizes, hidden?: HiddenFrom, room?: Room): Promise<CamFields> {
  const { list, picked } = await shotCandidates(m, sizes, hidden, room);
  return list[picked].cam;
}
